import type {
    DataTableColumn,
    FilterItemConfig,
    OptionItem,
} from '@/components/ui/data-table-customized'
import {
    createCurrencyFormatter,
    createDateFormatter,
    createInternalLinkFormatter,
    createOptionsFormatter,
} from '@/components/ui/data-table-customized'
import { TFunction } from 'i18next'
import type { Supplier } from '../types'

// ============================================================================
// Status Options
// ============================================================================

export const SUPPLIER_STATUS_OPTIONS = (t: TFunction): OptionItem[] => [
    { label: t('active'), value: 'active', color: 'success' },
    { label: t('inactive'), value: 'inactive', color: 'warning' },
    { label: t('suspended'), value: 'suspended', color: 'info' },
]

// ============================================================================
// Column Configuration
// ============================================================================

export const createSupplierColumns = (
    t: TFunction
): DataTableColumn<Supplier>[] => [
    {
        key: 'partner_code',
        title: t('partner_code'),
        width: 150,
        sortable: true,
        pinned: 'left',
        formatter: createInternalLinkFormatter<Supplier>({
            pathKey: 'supplierDetail',
            pathParams: (_, row) => ({ id: row.partner_id }),
        }),
    },
    {
        key: 'partner_name',
        title: t('partner_name'),
        width: 250,
        sortable: true,
    },
    {
        key: 'partner_email',
        title: t('partner_email'),
        width: 200,
    },
    {
        key: 'partner_phone',
        title: t('partner_phone'),
        width: 140,
    },
    {
        key: 'partner_tax_code',
        title: t('partner_tax_code'),
        width: 140,
    },
    {
        key: 'partner_status',
        title: t('status'),
        width: 150,
        sortable: true,
        formatter: createOptionsFormatter({
            options: SUPPLIER_STATUS_OPTIONS(t),
        }),
    },
    {
        key: 'total_contracts',
        title: t('total_contracts'),
        width: 120,
        cellClassName: 'text-right',
    },
    {
        key: 'total_contract_value',
        title: t('total_contract_value'),
        width: 160,
        cellClassName: 'text-right',
        formatter: createCurrencyFormatter({ currency: 'đ' }),
    },
    {
        key: 'employee_name',
        title: t('manager'),
        width: 180,
    },
    {
        key: 'created_at',
        title: t('created_at'),
        width: 140,
        sortable: true,
        cellClassName: 'text-right',
        formatter: createDateFormatter({ format: 'DD/MM/YYYY' }),
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

export const createSupplierFilters = (t: TFunction): FilterItemConfig[] => [
    {
        name: 'partner_status',
        type: 'multi-select',
        label: t('status'),
        options: SUPPLIER_STATUS_OPTIONS(t).map((status) => ({
            value: status.value,
            label: status.label,
        })),
        placeholder: t('select-status'),
    },
]

// ============================================================================
// Constants
// ============================================================================

export const SUPPLIER_ALLOWED_SORT_FIELDS = [
    'partner_id',
    'partner_name',
    'partner_code',
    'partner_status',
    'created_at',
]

export interface SupplierFilters {
    partner_status: string[]
}

export const SUPPLIER_FILTER_DEFAULTS: SupplierFilters = {
    partner_status: [],
}
